// Formatting of the mock case: money from integer cents, dates from ISO days. The real formatting library is
// task L4-04; these three functions keep every screen on the same German forms until then.

const eur = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
const dayMonth = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit" });
const longDate = new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const shortDate = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
const names = new Intl.ListFormat("de-DE", { style: "long", type: "conjunction" });
const percent = new Intl.NumberFormat("de-DE", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Integer cents to German euros: 416500 becomes "4.165,00 €". */
export function formatEUR(cents: number): string {
  return eur.format(cents / 100);
}

/** An ISO day to the task form: "2026-10-13" becomes "13.10.". Parsed as a local day, never UTC midnight. */
export function formatDay(dateISO: string): string {
  return dayMonth.format(parseDay(dateISO));
}

/** An ISO day to the long form: "2026-10-04" becomes "Sonntag, 4. Oktober 2026". */
export function formatLong(dateISO: string): string {
  return longDate.format(parseDay(dateISO));
}

/** An ISO day to the short form: "2026-10-04" becomes "04.10.2026". */
export function formatShort(dateISO: string): string {
  return shortDate.format(parseDay(dateISO));
}

/** Whole days from one ISO day to another, counted on the calendar, not the clock. */
export function daysBetween(fromISO: string, toISO: string): number {
  const [fromYear, fromMonth, fromDay] = parseParts(fromISO);
  const [toYear, toMonth, toDay] = parseParts(toISO);
  const from = Date.UTC(fromYear, fromMonth - 1, fromDay);
  const to = Date.UTC(toYear, toMonth - 1, toDay);
  return Math.round((to - from) / 86_400_000);
}

/** German list joining: ["a", "b"] becomes "a und b". */
export function joinNames(values: ReadonlyArray<string>): string {
  return names.format(values);
}

/** A fraction to German percent: 0.0383 becomes "3,83 %". */
export function formatPercent(fraction: number): string {
  return percent.format(fraction);
}

/** An ISO day shifted by whole days, back as an ISO day. Counts on the calendar, not the clock. */
export function plusDays(dateISO: string, days: number): string {
  const [year, month, day] = parseParts(dateISO);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}

function parseParts(dateISO: string): [number, number, number] {
  const parts = dateISO.split("-").map((part) => Number(part));
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];
  if (year === undefined || month === undefined || day === undefined || !parts.every(Number.isInteger)) {
    throw new Error(`Expected an ISO day (yyyy-mm-dd), not "${dateISO}".`);
  }
  return [year, month, day];
}

function parseDay(dateISO: string): Date {
  const [year, month, day] = parseParts(dateISO);
  return new Date(year, month - 1, day);
}
